import { useDynamicSmartOfferStore } from "@expert/sales-tools";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { Button, Divider, Group, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

type Props = {
    sessionId: string;
};

const TONE_GUIDANCE_PRESETS = [
    "Empathize with busy family life. Focus on simplicity and future-proofing their growing tech needs.",
    "Acknowledge their concern about cost. Emphasize value and the single monthly price.",
    "They're tech-savvy — lead with breadth of coverage and automatic inclusion of new devices.",
];

const PITCH_POINT_PRESETS: string[][] = [
    [
        "As your kids grow, Verizon Home Device Protect automatically covers new eligible tablets or gaming devices — no need to add them later.",
        "If accidental damage occurs on your laptop, we'll repair, replace, or reimburse for covered breakdowns.",
        "One plan protects your tablet, laptop, and eligible future tech — no juggling multiple plans.",
    ],
    [
        "This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased!",
        "The products you currently own are covered and future purchases are automatically included; No receipts or registration required",
        "A service fee of no more than $99+ tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.",
    ],
];

const EMIT_SUCCESS_DURATION_MS = 2000;

export function DynamicSmartOfferTab({ sessionId }: Props) {
    const [toneGuidance, setToneGuidance] = useState("");
    const [pitchPoint1, setPitchPoint1] = useState("");
    const [pitchPoint2, setPitchPoint2] = useState("");
    const [pitchPoint3, setPitchPoint3] = useState("");
    const [showEmitted, setShowEmitted] = useState(false);

    const canEmit = toneGuidance && pitchPoint1;

    useEffect(() => {
        if (!showEmitted) return;
        const id = setTimeout(() => setShowEmitted(false), EMIT_SUCCESS_DURATION_MS);
        return () => clearTimeout(id);
    }, [showEmitted]);

    const emit = useCallback(() => {
        if (!canEmit) return;
        expertWorkspaceWebSocketEventBus.emit("ws_dynamic-smart-offer-event", {
            sessionId,
            toneGuidance,
            pitchPoints: [pitchPoint1, pitchPoint2, pitchPoint3].filter(Boolean),
        });
        setShowEmitted(true);
    }, [canEmit, sessionId, toneGuidance, pitchPoint1, pitchPoint2, pitchPoint3]);

    return (
        <Stack gap="sm">
            <Divider label="Tone Guidance" labelPosition="left" />

            <Group gap="xs">
                {TONE_GUIDANCE_PRESETS.map((preset, i) => (
                    <Button key={i} size="xs" variant="light" color="violet" onClick={() => setToneGuidance(preset)}>
                        Preset {i + 1}
                    </Button>
                ))}
            </Group>

            <Textarea
                placeholder="Tone guidance text..."
                value={toneGuidance}
                onChange={(e) => setToneGuidance(e.target.value)}
                size="xs"
                autosize
                minRows={2}
            />

            <Divider label="Pitch Points" labelPosition="left" />

            <Group gap="xs">
                {PITCH_POINT_PRESETS.map((preset, i) => (
                    <Button
                        key={i}
                        size="xs"
                        variant="light"
                        color="violet"
                        onClick={() => {
                            setPitchPoint1(preset[0]);
                            setPitchPoint2(preset[1]);
                            setPitchPoint3(preset[2]);
                        }}
                    >
                        Preset {i + 1}
                    </Button>
                ))}
            </Group>

            <TextInput
                placeholder="Pitch point 1"
                value={pitchPoint1}
                onChange={(e) => setPitchPoint1(e.target.value)}
                size="xs"
            />
            <TextInput
                placeholder="Pitch point 2"
                value={pitchPoint2}
                onChange={(e) => setPitchPoint2(e.target.value)}
                size="xs"
            />
            <TextInput
                placeholder="Pitch point 3"
                value={pitchPoint3}
                onChange={(e) => setPitchPoint3(e.target.value)}
                size="xs"
            />

            <Group gap="xs" align="center">
                <Button size="xs" variant="filled" color="violet" onClick={emit} disabled={!canEmit}>
                    Emit Dynamic Smart Offer
                </Button>
                <Button
                    size="xs"
                    variant="light"
                    color="red.5"
                    onClick={() => {
                        // The store has a real `reset` action; this used to write an empty
                        // offer through `receiveData` instead, which left the session entry in
                        // place. It also never cleared this panel's own fields, so after
                        // "Reset" the form still showed the values you had just emitted.
                        useDynamicSmartOfferStore.getState().reset(sessionId);
                        setToneGuidance("");
                        setPitchPoint1("");
                        setPitchPoint2("");
                        setPitchPoint3("");
                        setShowEmitted(true);
                    }}
                >
                    Reset
                </Button>
                {showEmitted && (
                    <Text size="xs" c="green.3" fw={500}>
                        Emitted
                    </Text>
                )}
            </Group>
        </Stack>
    );
}
